import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  approveHitlItem,
  fetchHitlInbox,
  rejectHitlItem,
  requestHitlChanges,
} from "@/api/hitl";
import type { HitlInboxFilters, HitlItem } from "@/types/hitl";
import { useToast } from "@/components/ToastProvider";
import { useEventStream, type SSEEvent } from "@/hooks/useEventStream";

type HitlAction = "approve" | "reject" | "request_changes" | null;

interface UseHitlInboxOptions {
  filters?: Partial<HitlInboxFilters>;
  subscribeToEvents?: boolean;
  toastOnUpdate?: boolean;
}

export function useHitlInbox(options: UseHitlInboxOptions = {}) {
  const { push } = useToast();
  const {
    filters: initialFilters = {},
    subscribeToEvents = true,
    toastOnUpdate = true,
  } = options;
  const [filters, setFilters] = useState<HitlInboxFilters>(() => ({
    category: initialFilters.category ?? "all",
    status: initialFilters.status ?? "pending",
    mineOnly: initialFilters.mineOnly ?? false,
    assigned_to: initialFilters.assigned_to ?? null,
    limit: initialFilters.limit,
  }));
  const filtersRef = useRef(filters);
  const hasLoadedRef = useRef(false);

  const [items, setItems] = useState<HitlItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionState, setActionState] = useState<Record<string, HitlAction>>({});

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const fetchWithFilters = useCallback(async (activeFilters: HitlInboxFilters) => {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchHitlInbox(activeFilters);
      setItems(payload);
      hasLoadedRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load HITL inbox.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchWithFilters(filters);
  }, [filters, fetchWithFilters]);

  const refresh = useCallback(async () => {
    await fetchWithFilters(filtersRef.current);
  }, [fetchWithFilters]);

  const optimisticUpdate = useCallback((id: string, updater: (item: HitlItem) => HitlItem) => {
    let snapshot: HitlItem | null = null;
    setItems((current) =>
      current.map((item) => {
        if (item.id === id) {
          snapshot = item;
          return updater(item);
        }
        return item;
      }),
    );
    return snapshot;
  }, []);

  const restoreSnapshot = useCallback((id: string, snapshot: HitlItem | null) => {
    if (!snapshot) return;
    setItems((current) => current.map((item) => (item.id === id ? snapshot : item)));
  }, []);

  const updateActionState = useCallback((id: string, action: HitlAction) => {
    setActionState((current) => ({ ...current, [id]: action }));
  }, []);

  const approve = useCallback(
    async (id: string, body?: Record<string, unknown>) => {
      const snapshot = optimisticUpdate(id, (item) => ({ ...item, status: "approved" }));
      updateActionState(id, "approve");
      try {
        await approveHitlItem(id, body);
        push({ title: "Approved", message: "HITL item marked as approved." });
      } catch (err) {
        restoreSnapshot(id, snapshot);
        push({
          title: "Unable to approve",
          message: err instanceof Error ? err.message : "Action failed.",
        });
        throw err;
      } finally {
        updateActionState(id, null);
      }
    },
    [optimisticUpdate, push, restoreSnapshot, updateActionState],
  );

  const reject = useCallback(
    async (id: string, reason?: string) => {
      const snapshot = optimisticUpdate(id, (item) => ({ ...item, status: "rejected" }));
      updateActionState(id, "reject");
      try {
        await rejectHitlItem(id, reason);
        push({ title: "Rejected", message: "HITL item rejected." });
      } catch (err) {
        restoreSnapshot(id, snapshot);
        push({
          title: "Unable to reject",
          message: err instanceof Error ? err.message : "Action failed.",
        });
        throw err;
      } finally {
        updateActionState(id, null);
      }
    },
    [optimisticUpdate, push, restoreSnapshot, updateActionState],
  );

  const requestChanges = useCallback(
    async (id: string, notes: string) => {
      const snapshot = optimisticUpdate(id, (item) => ({ ...item, status: "changes_requested" }));
      updateActionState(id, "request_changes");
      try {
        await requestHitlChanges(id, notes);
        push({ title: "Changes requested", message: "Sent back to agent with notes." });
      } catch (err) {
        restoreSnapshot(id, snapshot);
        push({
          title: "Unable to send feedback",
          message: err instanceof Error ? err.message : "Action failed.",
        });
        throw err;
      } finally {
        updateActionState(id, null);
      }
    },
    [optimisticUpdate, push, restoreSnapshot, updateActionState],
  );

  const handleEvent = useCallback(
    (event: SSEEvent) => {
      if (event.event !== "hitl_pending") return;
      if (toastOnUpdate && hasLoadedRef.current) {
        push({ title: "Inbox updated", message: "New HITL events are ready." });
      }
      void refresh();
    },
    [push, refresh, toastOnUpdate],
  );

  useEventStream(subscribeToEvents ? handleEvent : undefined, subscribeToEvents);

  return useMemo(
    () => ({
      items,
      loading,
      error,
      filters,
      setFilters,
      refresh,
      approve,
      reject,
      requestChanges,
      actionState,
    }),
    [items, loading, error, filters, refresh, approve, reject, requestChanges, actionState],
  );
}
